/**
 * ACT2-1108: one step of the setup panel. Only the expanded step is a card with its body. Every step,
 * in every state, reads as the same two lines: its title and one subtitle line, both muted once it is
 * done. A step's title is a button that opens or closes it, so a step can be done out of order. An open
 * done step ends with Continue, which closes it and opens the next step still to do.
 */
import { ArrowRight, CircleCheck } from 'lucide-react';
import { useId } from 'react';

import { Button } from '@/_shared/components/ui/button';
import type { SetupStepTag } from '@/_shared/lib/setup-steps';
import { cn } from '@/_shared/lib/utils';

interface SetupStepProps {
  n: number;
  title: string;
  tag?: SetupStepTag;
  /** What the step is for: the subtitle, unless a done step has a `doneNote`. */
  description: string;
  /** What a done step did, such as "Skipped" or the connected warehouse. Replaces the subtitle once done. */
  doneNote?: string;
  done: boolean;
  /** The first step still to do. */
  current: boolean;
  expanded: boolean;
  /** Absent for a step that cannot be opened. */
  onToggle?: () => void;
  /** Closes the step and opens the next one. Shown at the foot of the open step when present. */
  onContinue?: () => void;
  continueLabel?: string;
  children?: React.ReactNode;
}

const TAG_CLASSES: Record<SetupStepTag, string> = {
  Required: 'text-danger',
  Recommended: 'text-muted-foreground',
  Optional: 'text-muted-foreground',
};

export function SetupStep({
  n,
  title,
  tag,
  description,
  doneNote,
  done,
  current,
  expanded,
  onToggle,
  onContinue,
  continueLabel = 'Continue',
  children,
}: SetupStepProps): React.ReactElement {
  const bodyId = useId();
  const showBody = expanded && children !== undefined;

  const marker = done ? (
    <CircleCheck aria-hidden className="mt-0.5 size-4 shrink-0 text-success" />
  ) : (
    <span
      aria-hidden
      className={cn(
        'mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full border text-[0.625rem] leading-none tabular-nums',
        current ? 'border-primary font-medium text-primary' : 'border-grey-200 text-muted-foreground',
      )}
    >
      {n}
    </span>
  );

  // One line each, whatever the state, so every step is the same height.
  const subtitle = done && doneNote ? doneNote : description;
  const heading = (
    <span className="flex min-w-0 flex-1 flex-col gap-0.5 text-left">
      <span className="flex min-w-0 items-baseline gap-2">
        <span
          className={cn('min-w-0 truncate text-sm', done ? 'text-muted-foreground' : 'font-medium text-foreground')}
        >
          {title}
          {done && <span className="sr-only"> (done)</span>}
        </span>
        {!done && tag && <span className={cn('shrink-0 text-xs', TAG_CLASSES[tag])}>{tag}</span>}
      </span>
      <span className="truncate text-xs text-muted-foreground" title={subtitle}>
        {subtitle}
      </span>
    </span>
  );

  return (
    <li className={cn('rounded-lg border px-3', showBody ? 'border-border bg-card py-2.5' : 'border-transparent py-1')}>
      {onToggle ? (
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={showBody}
          aria-controls={showBody ? bodyId : undefined}
          className="flex w-full items-start gap-2 rounded-md py-0.5 outline-none focus-visible:shadow-primary"
        >
          {marker}
          {heading}
        </button>
      ) : (
        <div className="flex w-full items-start gap-2 py-0.5">
          {marker}
          {heading}
        </div>
      )}
      {showBody && (
        <div id={bodyId} className="mt-2 flex flex-col gap-3 pl-6">
          {children}
          {onContinue && (
            <div className="flex justify-end border-t border-border p-0.5 pt-3">
              <Button
                type="button"
                variant="primary"
                size="sm"
                label={continueLabel}
                suffixIcon={continueLabel === 'Continue' ? <ArrowRight /> : undefined}
                onClick={onContinue}
              />
            </div>
          )}
        </div>
      )}
    </li>
  );
}
